import { DO_CONTRACT_TYPES } from "@etudescarre/constants";
import { Button, Select } from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import { useYardContext } from "@/providers/yard-context";

// Écran 033 : dépôt direct d'un rapport d'examen par un donneur
// d'ordre. Les méta-données sont renseignées automatiquement : contrat
// émetteur = contrat DO du user, auteur = user, date de rédaction = date du
// jour ; le n° de rapport est auto-incrémenté sauf saisie manuelle.

type DepositReportFormData = {
  numero: string;
};

const inputClass =
  "w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100";

const labelClass =
  "mb-1 block text-sm font-medium text-zinc-900 dark:text-zinc-100";

const readOnlyClass =
  "w-full rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-700 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-300";

const doTypes: readonly string[] = DO_CONTRACT_TYPES;

type DepositReportPanelProps = {
  onClose: () => void;
};

export function DepositReportPanel({ onClose }: DepositReportPanelProps) {
  const { yard, employee, contracts } = useYardContext();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Contrat émetteur : récupéré automatiquement parmi les contrats donneur
  // d'ordre du user sur ce chantier. Un choix n'est demandé que s'il en a
  // plusieurs.
  const doContracts = useMemo(
    () => contracts.filter((c) => doTypes.includes(c.type)),
    [contracts],
  );
  const [selectedContractId, setSelectedContractId] = useState<number | null>(
    doContracts.length === 1 ? doContracts[0].id : null,
  );

  const form = useForm<DepositReportFormData>({
    defaultValues: { numero: "" },
  });

  const authorName =
    [employee.firstName, employee.lastName].filter(isNotNil).join(" ").trim() ||
    employee.id;

  const today = new Date().toLocaleDateString("fr-FR");

  const contractLabel = (c: (typeof contracts)[number]) =>
    `${c.performerCompanyName}${isNotNil(c.code) ? ` (${c.code})` : ""}`;

  const handleSubmit = form.handleSubmit(async (data) => {
    if (isNil(selectedContractId)) {
      toaster.error({
        title: "Erreur",
        description: "Sélectionnez le contrat émetteur",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const numero = data.numero.trim();
      const { data: result, error } = await apiClient.POST(
        "/api/yards/{yardId}/reports/deposit",
        {
          params: { path: { yardId: String(yard.id) } },
          body: {
            emetteurContractId: selectedContractId,
            ...(numero.length > 0 ? { numero } : {}),
          },
        },
      );

      if (error || isNil(result)) {
        toaster.error({
          title: "Erreur",
          description: error?.error ?? "Échec du dépôt du rapport",
        });
        return;
      }

      toaster.success({
        title: "Succès",
        description: `Rapport n° ${result.data.numero} déposé`,
      });
      onClose();
    } catch {
      toaster.error({
        title: "Erreur",
        description: "Une erreur est survenue",
      });
    } finally {
      setIsSubmitting(false);
    }
  });

  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={onClose}
        className="text-sm text-blue-600 hover:underline dark:text-blue-400"
      >
        Retour aux actions
      </button>

      <form onSubmit={handleSubmit} className="space-y-4">
        <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
          Déposer un rapport d'examen
        </h3>

        <div>
          <span className={labelClass}>Contrat émetteur</span>
          {doContracts.length === 1 ? (
            <p className={readOnlyClass}>{contractLabel(doContracts[0])}</p>
          ) : (
            <Select
              options={doContracts.map((c) => ({
                value: String(c.id),
                label: contractLabel(c),
              }))}
              value={
                isNotNil(selectedContractId) ? [String(selectedContractId)] : []
              }
              onValueChange={(details) => {
                const val = details.value[0];
                setSelectedContractId(isNotNil(val) ? Number(val) : null);
              }}
              placeholder="Sélectionnez votre contrat"
            />
          )}
        </div>

        <div>
          <span className={labelClass}>Auteur du rapport</span>
          <p className={readOnlyClass}>{authorName}</p>
        </div>

        <div>
          <span className={labelClass}>Date de rédaction</span>
          <p className={readOnlyClass}>{today}</p>
        </div>

        <div>
          <label htmlFor="depositReportNumero" className={labelClass}>
            N° rapport
          </label>
          <input
            id="depositReportNumero"
            type="text"
            {...form.register("numero")}
            maxLength={255}
            placeholder="Automatique"
            className={inputClass}
          />
          <p className="mt-1 text-xs text-zinc-500">
            Laissez vide pour une numérotation automatique.
          </p>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button
            type="button"
            outline
            onClick={onClose}
            disabled={isSubmitting}
          >
            Annuler
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "En cours..." : "Déposer"}
          </Button>
        </div>
      </form>
    </div>
  );
}
